import React, { useEffect, useState } from "react";
import { analyticsApi, applicationsApi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Users, TrendingUp, DollarSign, Sparkles, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DASHBOARD } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const KPI_DEFS = [
  { key: "open_jobs", label: "Open Requisitions", icon: Briefcase, delta: "+12%" },
  { key: "candidates", label: "Active Candidates", icon: Users, delta: "+8%" },
  { key: "placements", label: "Placements (MTD)", icon: TrendingUp, delta: "+21%" },
  { key: "revenue", label: "Revenue (MTD)", icon: DollarSign, delta: "+18%", format: (v) => "$" + (v / 1000).toFixed(1) + "k" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    analyticsApi.overview().then(setData);
    applicationsApi.list().then((r) => setRecent(r.slice(0, 8)));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Welcome back, {user?.name?.split(" ")[0]}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Recruiter Dashboard</h1>
        </div>
        <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-mono text-xs tracking-wider">
          <Sparkles className="h-3 w-3 mr-1" /> AI copilot online
        </Badge>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-border rounded-lg overflow-hidden bg-white">
        {KPI_DEFS.map((k, i) => {
          const Icon = k.icon;
          const v = data.kpis[k.key];
          return (
            <div key={k.key} data-testid={DASHBOARD.kpiCard(k.key)} className={`p-6 ${i < 3 ? "lg:border-r border-border" : ""} ${i < 2 ? "border-b lg:border-b-0 border-border" : ""} ${i === 2 ? "lg:border-r" : ""}`}>
              <div className="flex items-center justify-between mb-4">
                <Icon className="h-5 w-5 text-[#0047AB]" strokeWidth={1.5} />
                <Badge variant="outline" className="text-[10px] font-mono">{k.delta}</Badge>
              </div>
              <div className="font-display text-3xl font-bold tracking-tight">{k.format ? k.format(v) : v}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Funnel */}
        <Card data-testid={DASHBOARD.funnelChart} className="lg:col-span-2 border-border shadow-none">
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">Pipeline Funnel</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Applications across stages</p>
            </div>
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">This month</span>
          </div>
          <div className="p-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="stage" tickLine={false} axisLine={false} className="text-xs" />
                <YAxis tickLine={false} axisLine={false} className="text-xs" />
                <Tooltip cursor={{ fill: "#F4F4F5" }} />
                <Bar dataKey="count" fill="#0047AB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Activity */}
        <Card data-testid={DASHBOARD.activityFeed} className="border-border shadow-none">
          <div className="p-6 border-b border-border">
            <h3 className="font-display text-lg font-bold tracking-tight">Recent Pipeline Activity</h3>
          </div>
          <div className="divide-y divide-border max-h-96 overflow-y-auto">
            {recent.map((a) => (
              <div key={a.id} className="p-4 hover:bg-[#FAFAFA] transition-colors">
                <div className="flex items-start gap-3">
                  {a.candidate?.avatar ? (
                    <img src={a.candidate.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#F4F4F5] grid place-items-center text-xs font-bold">{a.candidate?.name?.[0] || "?"}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.candidate?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.job?.title} · {a.job?.client}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] uppercase font-mono">{a.stage}</Badge>
                      <span className="text-xs text-[#4338CA] font-mono">{a.match_score}% match</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* AI Recommendations */}
      <Card className="border-border shadow-none overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between bg-gradient-to-r from-white to-indigo-50/40">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-[#4338CA] text-white grid place-items-center"><Sparkles className="h-4 w-4" /></div>
            <div>
              <h3 className="font-display text-lg font-bold tracking-tight">AI Copilot · Today's Priorities</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Human review required before action</p>
            </div>
          </div>
          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-50 font-mono text-[10px] tracking-wider">3 pending</Badge>
        </div>
        <div className="divide-y divide-border">
          {[
            { title: "Fast-track Ava Patel to interview", reason: "97% match on ML Platform Engineer · 4 mutual skills", cta: "Review" },
            { title: "Reach out to Noah Nguyen", reason: "Passive candidate flagged by CRM signal · last active 12d ago", cta: "Draft outreach" },
            { title: "Summarize 3 pending interview loops", reason: "Interviewer feedback ready for Northwind Robotics roles", cta: "Summarize" },
          ].map((r, i) => (
            <div key={i} className="p-5 flex items-center justify-between hover:bg-[#FAFAFA]">
              <div>
                <div className="font-semibold">{r.title}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{r.reason}</div>
              </div>
              <button className="text-sm text-[#4338CA] font-semibold hover:underline flex items-center gap-1">{r.cta} <ArrowUpRight className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
